import { FormEvent, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import { useAuth } from '../../auth/useAuth';

const SITEKEY = import.meta.env.VITE_TURNSTILE_SITEKEY ?? '0x4AAAAAADGmUJBjcx4j6XEz';

// T-shirt size catalog — kept in sync with functions/src/shared/types.ts.
type TshirtSize = 'XS' | 'S' | 'M' | 'L' | 'XL' | 'XXL' | 'XXXL';
type YouthSize  = 'Y-XS' | 'Y-S' | 'Y-M' | 'Y-L' | 'Y-XL';
const ADULT_SIZES: TshirtSize[] = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
const YOUTH_SIZES: YouthSize[]  = ['Y-XS', 'Y-S', 'Y-M', 'Y-L', 'Y-XL'];

interface AttendeeRow {
  name: string;
  type: 'adult' | 'child';
  tshirtSize?: TshirtSize | YouthSize;
  golfing?: boolean;
}

interface SubmitResponse {
  ok?: boolean;
  message?: string;
  error?: string;
  _devMagicLink?: string;
}

interface EventToggles {
  tshirtsEnabled: boolean;
  golfEnabled: boolean;
}

/** Frontend error state. `code` lets us render specific UIs (e.g. sign-in CTA
 *  for the EmployeeAlreadyRsvpd 409). */
interface FormError {
  message: string;
  code?: string;
}

function employeeHint(t: EventToggles): string | null {
  if (t.tshirtsEnabled) return 'Employees get a t-shirt.';
  return null;
}

function signInCaption(t: EventToggles): string {
  const base = "Don't have a company email? Just keep going below";
  if (t.tshirtsEnabled && t.golfEnabled) return `${base} — you'll still get a shirt and a tee time.`;
  if (t.tshirtsEnabled) return `${base} — you'll still get a shirt.`;
  if (t.golfEnabled) return `${base} — you can still sign up for the tournament.`;
  return `${base}.`;
}

function rebuildAttendees(
  adults: number,
  children: number,
  prior: AttendeeRow[],
): AttendeeRow[] {
  const out: AttendeeRow[] = [];
  for (let i = 0; i < adults; i++) {
    const prev = prior[i]?.type === 'adult' ? prior[i] : undefined;
    out.push({
      name: prev?.name ?? '',
      type: 'adult',
      tshirtSize: (prev?.tshirtSize as TshirtSize | undefined) ?? 'M',
      golfing: prev?.golfing ?? false,
    });
  }
  const priorChildren = prior.filter(r => r.type === 'child');
  for (let i = 0; i < children; i++) {
    const prev = priorChildren[i];
    out.push({
      name: prev?.name ?? '',
      type: 'child',
      tshirtSize: (prev?.tshirtSize as YouthSize | undefined) ?? 'Y-M',
    });
  }
  return out;
}

export function Rsvp() {
  const { isSignedIn, account, getApiToken, signIn } = useAuth();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail]             = useState('');
  const [attending, setAttending]     = useState(true);
  const [adults, setAdults]           = useState(1);
  const [children, setChildren]       = useState(0);
  const [attendees, setAttendees]     = useState<AttendeeRow[]>([
    { name: '', type: 'adult', tshirtSize: 'M', golfing: false },
  ]);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [busy, setBusy]               = useState(false);
  const [result, setResult]           = useState<SubmitResponse | null>(null);
  const [formError, setFormError]     = useState<FormError | null>(null);

  const [isEmployee, setIsEmployee]   = useState(false);

  const [eventToggles, setEventToggles] = useState<EventToggles>({
    tshirtsEnabled: false,
    golfEnabled: false,
  });

  useEffect(() => {
    if (isSignedIn) navigate('/my-rsvp', { replace: true });
  }, [isSignedIn, navigate]);

  useEffect(() => {
    if (isSignedIn && account) {
      setDisplayName(account.name ?? '');
      const claims = account.idTokenClaims as { email?: string; preferred_username?: string } | undefined;
      setEmail(claims?.email ?? claims?.preferred_username ?? account.username ?? '');
    }
  }, [isSignedIn, account]);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/public/event')
      .then(r => (r.ok ? r.json() : null))
      .then(ev => {
        if (cancelled || !ev) return;
        setEventToggles({
          tshirtsEnabled: !!ev.tshirtsEnabled,
          golfEnabled: !!ev.golfEnabled,
        });
      })
      .catch(() => { });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setAttendees(prev => rebuildAttendees(adults, children, prev));
  }, [adults, children]);

  useEffect(() => {
    setAttendees(prev => {
      if (prev.length === 0) return prev;
      if (prev[0].name === displayName) return prev;
      const next = [...prev];
      next[0] = { ...next[0], name: displayName };
      return next;
    });
  }, [displayName]);

  if (isSignedIn) return null;

  function updateRow<K extends keyof AttendeeRow>(idx: number, key: K, val: AttendeeRow[K]) {
    setAttendees(prev => prev.map((row, i) => (i === idx ? { ...row, [key]: val } : row)));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isSignedIn && !turnstileToken) {
      setFormError({ message: 'Please complete the verification challenge.' });
      return;
    }
    setBusy(true);
    setResult(null);
    setFormError(null);
    try {
      const partySize = attending ? adults + children : 0;

      const sendTshirts = isEmployee && eventToggles.tshirtsEnabled && attending;
      const sendGolf    = eventToggles.golfEnabled && attending;

      const payloadAttendees = attending ? attendees.map(row => {
        const out: AttendeeRow = { name: row.name.trim(), type: row.type };
        if (sendTshirts && row.tshirtSize) out.tshirtSize = row.tshirtSize;
        if (sendGolf && row.type === 'adult') out.golfing = row.golfing === true;
        return out;
      }) : [];

      const headers: Record<string, string> = { 'content-type': 'application/json' };
      let endpoint = '/api/public/rsvp';

      if (isSignedIn) {
        const token = await getApiToken();
        headers['Authorization'] = `Bearer ${token}`;
        endpoint = '/api/me/rsvp';
      }

      const res = await fetch(endpoint, {
        method: isSignedIn ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify({
          email,
          displayName,
          attending,
          partySize,
          adultsCount: attending ? adults : 0,
          childrenCount: attending ? children : 0,
          isEmployee,
          attendees: attending ? payloadAttendees : undefined,
          turnstileToken: isSignedIn ? undefined : turnstileToken,
        }),
      });
      const body = (await res.json()) as SubmitResponse;
      if (!res.ok) {
        setFormError({
          message: body.message ?? body.error ?? `Request failed (${res.status})`,
          code: body.error,
        });
      } else {
        setResult(body);
      }
    } catch (err) {
      setFormError({ message: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  if (result?.ok) {
    return <PendingScreen result={result} email={email} isEmployee={isSignedIn} />;
  }

  // Special-case the "already RSVP'd as employee" 409 — show a CTA-style
  // panel instead of the generic alert bar so the user knows what to do.
  const showEmployeeAlreadyRsvpd = formError?.code === 'EmployeeAlreadyRsvpd';

  return (
    <section className="section">
      <div className="container rsvp">
        <span className="eyebrow">— RSVP</span>
        <h1>Tell us you're coming</h1>
        <p className="rsvp__lede">
          One RSVP per household. Spouses, significant others, kids, retirees, and friends of the company are all welcome &mdash; bring 'em.
        </p>

        <form onSubmit={onSubmit} className="rsvp__form">
          <div className="field-group">
            <span className="field-group__label">Are you a WIC employee?</span>
            {employeeHint(eventToggles) && (
              <p className="caption" style={{ marginTop: '-0.2rem' }}>
                {employeeHint(eventToggles)}
              </p>
            )}
            <div className="toggle">
              <ToggleOption
                checked={isEmployee}
                onChange={() => setIsEmployee(true)}
                label="Yes, I'm a WIC employee"
                accent
              />
              <ToggleOption
                checked={!isEmployee}
                onChange={() => setIsEmployee(false)}
                label="No, I'm a guest"
              />
            </div>
          </div>

          {isEmployee && (
            <div className="signin-card">
              <span className="signin-card__eyebrow">— Have a westerncon.com email?</span>
              <h3 className="signin-card__title">Sign in for the full app</h3>
              <p className="signin-card__body">
                Signing in with your <strong>@westerncon.com</strong> account unlocks
                the photo gallery from past picnics &mdash; and lets you upload your own
                pictures from this year.
              </p>
              <button
                type="button"
                className="signin-card__btn"
                onClick={() => signIn()}
              >
                Sign in with Microsoft
              </button>
              <p className="caption signin-card__caption">{signInCaption(eventToggles)}</p>
            </div>
          )}

          <div className="field-group">
            <span className="field-group__label">Who are you?</span>
            <div className="grid-2">
              <div>
                <label htmlFor="displayName">Your name</label>
                <input
                  id="displayName"
                  type="text"
                  required
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  maxLength={120}
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  maxLength={254}
                  placeholder="jane@example.com"
                />
                <p className="caption" style={{ marginTop: '0.4rem' }}>
                  We'll send a confirmation link here.
                </p>
              </div>
            </div>
          </div>

          <div className="field-group">
            <span className="field-group__label">Will you make it?</span>
            <div className="toggle">
              <ToggleOption checked={attending}  onChange={() => setAttending(true)}  label="Yes, count us in" accent />
              <ToggleOption checked={!attending} onChange={() => setAttending(false)} label="No, can't make it" />
            </div>
          </div>

          {attending && (
            <>
              <div className="field-group">
                <span className="field-group__label">Your party</span>
                <div className="party-counters">
                  <PartyCounter
                    label="Adults"
                    sublabel="Including yourself"
                    value={adults}
                    min={1}
                    onChange={setAdults}
                  />
                  <PartyCounter
                    label="Children"
                    sublabel="Under 18"
                    value={children}
                    min={0}
                    onChange={setChildren}
                  />
                </div>
              </div>

              <div className="field-group">
                <span className="field-group__label">Attendees</span>
                <p className="caption" style={{ marginTop: '-0.2rem' }}>
                  Names help with nametags and check-in. Kids' names are optional.
                </p>
                <div className="attendees-grid">
                  {attendees.map((row, i) => {
                    const isAdult = row.type === 'adult';
                    const adultIdx = attendees.slice(0, i + 1).filter(r => r.type === 'adult').length;
                    const childIdx = attendees.slice(0, i + 1).filter(r => r.type === 'child').length;
                    const label =
                      i === 0 ? 'You' :
                      isAdult ? `Adult ${adultIdx}` :
                                `Child ${childIdx}`;

                    return (
                      <div key={i} className="attendee-row">
                        <span className="attendee-row__who">{label}</span>
                        <input
                          className="attendee-row__name"
                          type="text"
                          value={row.name}
                          onChange={e => updateRow(i, 'name', e.target.value)}
                          maxLength={120}
                          placeholder={isAdult ? 'Name' : "Child's name (optional)"}
                          disabled={i === 0}
                          aria-label={`${label} name`}
                        />
                        {isEmployee && eventToggles.tshirtsEnabled && (
                          <select
                            className="attendee-row__select"
                            value={row.tshirtSize ?? (isAdult ? 'M' : 'Y-M')}
                            onChange={e => updateRow(i, 'tshirtSize', e.target.value as TshirtSize | YouthSize)}
                            aria-label={`${label} t-shirt size`}
                          >
                            {(isAdult ? ADULT_SIZES : YOUTH_SIZES).map(s => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        )}
                        {eventToggles.golfEnabled && isAdult && (
                          <label className="attendee-row__golf">
                            <input
                              type="checkbox"
                              checked={row.golfing === true}
                              onChange={e => updateRow(i, 'golfing', e.target.checked)}
                            />
                            <span>⛳ Golfing</span>
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
                {eventToggles.golfEnabled && (
                  <p className="caption" style={{ marginTop: '0.5rem' }}>
                    Golf — Riverdale Dunes, 8:30 AM shotgun the morning of WICnic. WIC covers green fees and carts.
                  </p>
                )}
              </div>
            </>
          )}

          <div className="field-group">
            <span className="field-group__label">Verify</span>
            <Turnstile
              siteKey={SITEKEY}
              onSuccess={t => setTurnstileToken(t)}
              onExpire={() => setTurnstileToken(null)}
              onError={() => setTurnstileToken(null)}
            />
          </div>

          {/* Specific UI for the EmployeeAlreadyRsvpd 409 — surface the
              sign-in CTA prominently so the user has a clear next step. */}
          {showEmployeeAlreadyRsvpd ? (
            <div className="dedup-card">
              <span className="dedup-card__eyebrow">— Already RSVP'd</span>
              <h3 className="dedup-card__title">Sign in to update your RSVP</h3>
              <p className="dedup-card__body">{formError?.message}</p>
              <button
                type="button"
                className="dedup-card__btn"
                onClick={() => signIn()}
              >
                Sign in with Microsoft
              </button>
            </div>
          ) : formError ? (
            <div className="alert">{formError.message}</div>
          ) : null}

          <div className="rsvp__submit">
            <button type="submit" className="btn" disabled={busy}>
              {busy ? 'Sending…' : 'Submit RSVP'}
            </button>
            <p className="caption">
              You'll get an email to confirm. If you don't see it within a couple of minutes, check spam.
            </p>
          </div>
        </form>

        <style>{`
          .rsvp { max-width: 720px; }
          .rsvp__lede { font-size: 1.05rem; color: var(--steel); max-width: 60ch; margin: 1rem 0 2.5rem 0; }
          .rsvp__form { display: flex; flex-direction: column; gap: 2.25rem; margin-top: 1rem; }
          .field-group { display: flex; flex-direction: column; gap: 0.6rem; }
          .field-group__label {
            font-family: var(--font-display); font-weight: 500; font-size: 0.7rem;
            letter-spacing: 0.3em; text-transform: uppercase; color: var(--red);
          }
          .field-group__label::before { content: '— '; }
          .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
          @media (max-width: 640px) { .grid-2 { grid-template-columns: 1fr; } }
          .toggle { display: flex; gap: 0.75rem; flex-wrap: wrap; }
          .party-counters { display: flex; gap: 2rem; flex-wrap: wrap; }
          .alert { border-left: 4px solid var(--red); background: var(--bg); color: var(--steel); padding: 0.9rem 1.1rem; font-size: 0.95rem; }
          .rsvp__submit { display: flex; flex-direction: column; gap: 0.6rem; margin-top: 1rem; }

          /* Sign-in CTA */
          .signin-card {
            border: 2px solid var(--red);
            background: var(--bg);
            padding: 1.5rem 1.5rem 1.4rem;
            display: flex; flex-direction: column; gap: 0.65rem;
          }
          .signin-card__eyebrow {
            font-family: var(--font-display); font-weight: 500; font-size: 0.7rem;
            letter-spacing: 0.3em; text-transform: uppercase; color: var(--red);
          }
          .signin-card__title {
            font-family: var(--font-display); font-weight: 700; font-size: 1.4rem;
            text-transform: uppercase; letter-spacing: 0.02em; color: var(--black);
            margin: 0;
          }
          .signin-card__body { color: var(--steel); font-size: 0.95rem; margin: 0; }
          .signin-card__btn {
            align-self: flex-start;
            font-family: var(--font-display); font-weight: 600; font-size: 0.85rem;
            letter-spacing: 0.1em; text-transform: uppercase;
            background: var(--red); color: var(--white);
            border: none; padding: 0.75rem 1.4rem; cursor: pointer;
            transition: background 0.15s;
          }
          .signin-card__btn:hover { background: var(--red-dark); }
          .signin-card__caption { margin-top: 0.25rem; }

          /* Dedup error card — shown when the public form is blocked by
             an existing employee RSVP for the same email. */
          .dedup-card {
            border: 2px solid var(--red);
            background: #fff5f3;
            padding: 1.5rem 1.5rem 1.4rem;
            display: flex; flex-direction: column; gap: 0.65rem;
          }
          .dedup-card__eyebrow {
            font-family: var(--font-display); font-weight: 500; font-size: 0.7rem;
            letter-spacing: 0.3em; text-transform: uppercase; color: var(--red);
          }
          .dedup-card__title {
            font-family: var(--font-display); font-weight: 700; font-size: 1.3rem;
            text-transform: uppercase; letter-spacing: 0.02em; color: var(--black);
            margin: 0;
          }
          .dedup-card__body { color: var(--steel); font-size: 0.95rem; margin: 0; }
          .dedup-card__btn {
            align-self: flex-start;
            font-family: var(--font-display); font-weight: 600; font-size: 0.85rem;
            letter-spacing: 0.1em; text-transform: uppercase;
            background: var(--red); color: var(--white);
            border: none; padding: 0.75rem 1.4rem; cursor: pointer;
            transition: background 0.15s;
          }
          .dedup-card__btn:hover { background: var(--red-dark); }

          /* Per-attendee rows */
          .attendees-grid {
            display: flex; flex-direction: column; gap: 0.6rem;
            margin-top: 0.4rem;
          }
          .attendee-row {
            display: grid;
            grid-template-columns: minmax(80px, 100px) 1fr auto auto;
            gap: 0.75rem;
            align-items: center;
            padding: 0.6rem 0.85rem;
            border: 2px solid var(--border);
            background: var(--white);
          }
          @media (max-width: 640px) {
            .attendee-row {
              grid-template-columns: 1fr;
              gap: 0.5rem;
            }
          }
          .attendee-row__who {
            font-family: var(--font-display); font-weight: 600; font-size: 0.85rem;
            text-transform: uppercase; letter-spacing: 0.05em; color: var(--charcoal);
          }
          .attendee-row__name {
            border: 1px solid var(--border);
            padding: 0.45rem 0.7rem;
            font-family: var(--font-body); font-size: 0.95rem;
            background: var(--white);
            min-width: 0;
          }
          .attendee-row__name:disabled {
            background: var(--bg); color: var(--steel); cursor: not-allowed;
          }
          .attendee-row__select {
            font-family: var(--font-body); font-size: 0.95rem;
            padding: 0.4rem 0.55rem; border: 1px solid var(--border);
            background: var(--white); cursor: pointer;
          }
          .attendee-row__golf {
            display: inline-flex; align-items: center; gap: 0.4rem;
            font-family: var(--font-body); font-size: 0.9rem;
            color: var(--steel); cursor: pointer;
            white-space: nowrap;
          }
          .attendee-row__golf input[type="checkbox"] {
            accent-color: var(--red); width: 1.05rem; height: 1.05rem; cursor: pointer;
          }
        `}</style>
      </div>
    </section>
  );
}

function PartyCounter({ label, sublabel, value, min, onChange }: {
  label: string; sublabel: string; value: number; min: number; onChange: (v: number) => void;
}) {
  return (
    <div className="party-counter">
      <div className="party-counter__labels">
        <span className="party-counter__label">{label}</span>
        <span className="party-counter__sub">{sublabel}</span>
      </div>
      <div className="party-counter__controls">
        <button type="button" onClick={() => onChange(Math.max(min, value - 1))} aria-label={`Decrease ${label}`}>−</button>
        <span className="party-counter__val">{value}</span>
        <button type="button" onClick={() => onChange(Math.min(20, value + 1))} aria-label={`Increase ${label}`}>+</button>
      </div>
      <style>{`
        .party-counter { display: flex; flex-direction: column; gap: 0.6rem; }
        .party-counter__labels { display: flex; flex-direction: column; gap: 0.1rem; }
        .party-counter__label { font-family: var(--font-display); font-weight: 600; font-size: 0.95rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--black); }
        .party-counter__sub { font-family: var(--font-body); font-size: 0.8rem; color: var(--muted); }
        .party-counter__controls { display: flex; align-items: center; gap: 0; border: 2px solid var(--border); width: fit-content; }
        .party-counter__controls button { width: 40px; height: 40px; border: none; background: var(--white); font-size: 1.25rem; cursor: pointer; color: var(--charcoal); transition: background 0.15s, color 0.15s; }
        .party-counter__controls button:hover { background: var(--red); color: var(--white); }
        .party-counter__val { min-width: 40px; text-align: center; font-family: var(--font-display); font-size: 1.1rem; font-weight: 700; color: var(--black); }
      `}</style>
    </div>
  );
}

function ToggleOption({ checked, onChange, label, accent }: {
  checked: boolean; onChange: () => void; label: string; accent?: boolean;
}) {
  return (
    <label className={`toggle-opt ${checked ? 'toggle-opt--on' : ''} ${accent ? 'toggle-opt--accent' : ''}`}>
      <input type="radio" checked={checked} onChange={onChange} />
      <span>{label}</span>
      <style>{`
        .toggle-opt { display: inline-flex; align-items: center; gap: 0.5rem; padding: 0.7rem 1.1rem; border: 2px solid var(--border); background: var(--white); cursor: pointer; font-family: var(--font-display); font-weight: 500; font-size: 0.85rem; letter-spacing: 0.1em; text-transform: uppercase; color: var(--charcoal); transition: border-color var(--transition), color var(--transition), background var(--transition); }
        .toggle-opt input { position: absolute; opacity: 0; width: 0; height: 0; }
        .toggle-opt--on { border-color: var(--red); color: var(--red); }
        .toggle-opt--on.toggle-opt--accent { background: var(--red); color: var(--white); }
        .toggle-opt:hover { border-color: var(--red); }
      `}</style>
    </label>
  );
}

function PendingScreen({ result, email, isEmployee }: { result: SubmitResponse; email: string; isEmployee: boolean }) {
  return (
    <section className="section">
      <div className="container pending">
        <span className="eyebrow">— You're In</span>
        <h1>RSVP received!</h1>
        <p className="pending__lede">
          {isEmployee
            ? "You're on the list. See you at WICnic!"
            : <>We sent a confirmation link to <strong>{maskedEmail(email)}</strong>. Click it to lock in your RSVP &mdash; the link expires in 24 hours.</>}
        </p>
        {result._devMagicLink && (
          <div className="dev-link">
            <p className="eyebrow" style={{ color: 'var(--charcoal)' }}>— Dev Mode</p>
            <a href={result._devMagicLink}>{result._devMagicLink}</a>
          </div>
        )}
        <style>{`
          .pending { max-width: 640px; }
          .pending__lede { font-size: 1.1rem; color: var(--steel); margin: 1rem 0 1.5rem 0; }
          .dev-link { margin-top: 2.5rem; padding: 1.25rem; background: var(--bg); border-left: 4px solid var(--charcoal); font-size: 0.9rem; word-break: break-all; }
          .dev-link a { display: block; margin-top: 0.5rem; }
        `}</style>
      </div>
    </section>
  );
}

function maskedEmail(e: string): string {
  const [local, domain] = e.split('@');
  if (!domain) return e;
  const masked = local.length <= 2 ? local[0] + '*' : local.slice(0, 2) + '***';
  return `${masked}@${domain}`;
}
